import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import { Spinner } from "@/components/ui/spinner";
import { useCreatorsStore } from "@/stores/creators/creatorsStore";
import {
  DollarSignIcon,
  HashIcon,
  PercentIcon,
} from "lucide-react";
import { useMemo } from "react";
import AnalyticsSummaryBar, { CreatorAnalytics } from "./AnalyticsSummaryBar";
import AnalyticsTable from "./AnalyticsTable";
import VideoAnalyticsTable from "./VideoAnalyticsTable";
import FunnelChart, { FunnelStep } from "@/components/ui/funnel-chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import React from "react";
import { useCampaignStore } from "@/stores";
import { isPostActivationStatus } from "@/constants/statuses";
import { useAuth } from "@clerk/react";
import { ChipsInput } from "@/components/ui/chips-input";

export default function AnalyticsTab() {
  const {
    isLoading,
    isLoadingHashtags,
    campaignAnalyticsData,
    selectedTab,
    hashtags,
    setSelectedTab,
    createHashtag,
    getHashtags,
    deleteHashtag
  } = useAnalyticsStore();
  const { campaignId, campaign } = useCampaignStore();
  const { creators } = useCreatorsStore();

  const { getToken } = useAuth();

  const handleCreateHashtag = async (tag: string) => {
    const token = await getToken();
    if (!token || !campaignId) return;
    await createHashtag(campaignId, tag, token);
  };

  const handleDeleteHashtag = async (tag: string) => {
    const token = await getToken();
    if (!token || !campaignId) return;
    await deleteHashtag(campaignId, tag, token);
  };

  React.useEffect(() => {
    const fetchHashtags = async () => {
      const token = await getToken();
      if (!token) return;
      if (campaignId) {
        await getHashtags(campaignId, token);
      }
    };
    fetchHashtags();

    if (!campaign?.use_dub) { 
      console.log("Campaign uses DUB, setting default tab to video.");
      setSelectedTab("video");
    }
  }, [campaignId, getHashtags, getToken, campaign?.use_dub]);

  // Filter post-activation creators (activated, in a stage/eve phase, or
  // completed). isPostActivationStatus, NOT the old !(s in STATUS_MAP) test —
  // eve phase statuses now live in STATUS_MAP and would otherwise drop
  // waiting-posting creators out of the video table.
  const postActivationCreators = useMemo(() => {
    return creators.filter((creator) => {
      const s = creator.status;
      return s && isPostActivationStatus(s);
    });
  }, [creators]);

  // Conversion funnel data
  const conversionFunnelData: FunnelStep[] = useMemo(() => {
    if (!campaignAnalyticsData?.length) return [];

    const totalClicks = campaignAnalyticsData.reduce(
      (sum: number, c: CreatorAnalytics) => sum + c.clicks,
      0
    );
    const totalLeads = campaignAnalyticsData.reduce(
      (sum: number, c: CreatorAnalytics) => sum + c.leads,
      0
    );
    const totalSales = campaignAnalyticsData.reduce(
      (sum: number, c: CreatorAnalytics) => sum + c.sales,
      0
    );

    const leadsPercent = totalClicks > 0 ? (totalLeads / totalClicks) * 100 : 0;
    const salesPercent = totalClicks > 0 ? (totalSales / totalClicks) * 100 : 0;

    return [
      { label: "Clicks", value: 100, color: "dark_orange" },
      { label: "Leads", value: leadsPercent, color: "orange" },
      { label: "Sales", value: salesPercent, color: "light_orange" },
    ];
  }, [campaignAnalyticsData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner className="size-4" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full max-w-7xl mx-auto p-5 gap-2 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
      <div className="flex flex-col border rounded-lg shadow-xs relative overflow-hidden shrink-0">
        <AnalyticsSummaryBar data={campaignAnalyticsData || []} />
        {campaign?.use_dub && <Tabs
          value={selectedTab}
          onValueChange={(value) =>
            setSelectedTab(value as "conversion" | "video")
          }
          className="absolute top-3 right-3"
        >
          <TabsList className="border bg-background shadow-xs gap-0.5">
            <TabsTrigger
              value="conversion"
              className="hover:bg-sidebar text-xs text-muted-foreground data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 h-7 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none hover:text-foreground"
            >
              <DollarSignIcon className="size-3.5" strokeWidth={2.5} />
            </TabsTrigger>
            <TabsTrigger
              value="video"
              className="hover:bg-sidebar text-xs text-muted-foreground data-[state=active]:hover:bg-primary/10 relative after:absolute after:inset-x-0 after:bottom-0 h-7 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none hover:text-foreground"
            >
              <PercentIcon className="size-3.5" strokeWidth={2.5} />
            </TabsTrigger>
          </TabsList>
        </Tabs>}
        <AnimatePresence mode="wait">
          {selectedTab === "conversion" ? (
            <div
              key="funnel"
              className="animate-in fade-in-0 slide-in-from-top-2 duration-300"
            >
              <FunnelChart steps={conversionFunnelData} height={85} />
            </div>
          ) : (
            <div
              key="hashtags"
              className="animate-in fade-in-0 slide-in-from-top-2 duration-300 border-t border-dashed p-4 bg-sidebar"
            >
              <div className="text-sm font-medium uppercase tracking-wider mb-0.5">
                Campaign Hashtags
              </div>
              <span className="text-xs text-muted-foreground mb-4 block">
                These hashtags will be tracked in your video analytics.
              </span>
              <ChipsInput
                items={hashtags ?? []}
                onAdd={handleCreateHashtag}
                onRemove={handleDeleteHashtag}
                addLabel="Add Hashtag"
                placeholder="hashtag"
                chipIcon={<HashIcon className="size-3" />}
                inputIcon={<HashIcon size={14} aria-hidden="true" />}
                transform={(v) => v.replace(/#/g, "")}
                maxItems={10}
                maxItemsMessage="Maximum of 10 hashtags allowed"
                duplicateMessage="Hashtag already exists"
                isLoading={isLoadingHashtags}
              />
            </div>
          )}
        </AnimatePresence>
      </div>

      <motion.div
        className="flex-1 flex flex-col gap-2"
        layout
        transition={{ duration: 0.25, ease: "easeInOut" }}
      >
        {selectedTab === "conversion" ? (
          <AnalyticsTable data={campaignAnalyticsData || []} />
        ) : (
          <VideoAnalyticsTable creators={postActivationCreators} />
        )}
      </motion.div>
    </div>
  );
}
